import type { ServiceDetail } from "./types";

export const SPECIALIST_SERVICES_DATA: Record<string, ServiceDetail> = {
  "architectural-lighting": {
    slug: "architectural-lighting",
    title: "Lighting - Abacus Luminaire",
    seoTitle: "Architectural Lighting Solutions in Bahrain",
    seoDescription:
      "Bespoke architectural lighting and LED solutions designed and manufactured in-house in Bahrain. High-CRI retail lighting, chandeliers and DALI control systems.",
    category: "Specialist Division",
    subtitle:
      "Custom architectural lighting and bespoke LED solutions where design meets subtle alchemy.",
    description:
      "Design and manufacture of bespoke lighting, LED solutions, and custom fixtures. 'Design is a subtle alchemy - lighting is its key.'",
    bullets: [
      "High-CRI Retail Display Lighting",
      "Custom Architectural Luminaires",
      "Smart Scene & DALI Automation",
    ],
    imageAlt:
      "Architectural lighting fixture design Bahrain - Abacus Luminaire",
    heroImage:
      "https://vibe.filesafe.space/1785525013889768799/attachments/86115a81-5b7e-4088-9d86-2a4bff33496f.png",
    gallery: [
      "https://vibe.filesafe.space/1785525013889768799/attachments/4c99a7dc-d8ef-4579-804a-f429f9e1dc87.png",
      "https://assets.cdn.filesafe.space/TkZSxIjkCDLzrMPXgP9Q/media/6aa452b69f8b31b6abb96320.jpg",
      "https://assets.cdn.filesafe.space/TkZSxIjkCDLzrMPXgP9Q/media/6aa452b6a65c58fb9c45bd23.png",
      "https://assets.cdn.filesafe.space/TkZSxIjkCDLzrMPXgP9Q/media/6aa452b6a65c58fb9c45bd24.jpg",
      "https://assets.cdn.filesafe.space/TkZSxIjkCDLzrMPXgP9Q/media/6aa452b69f8b31b6abb9632b.jpg",
      "https://assets.cdn.filesafe.space/TkZSxIjkCDLzrMPXgP9Q/media/6aa452b685678ef96c595a0a.png",
    ],
    iconName: "Lightbulb",
    overview: [
      "Lighting is the soul of spatial design. Under our specialized 'Abacus Luminaire' division, we design, manufacture, and assemble bespoke architectural lights, linear magnetic tracks, chandeliers, and high-CRI retail spotlights.",
      "Whether casting an inviting warm glow over a luxury jewelry display or programming dynamic DALI scene controllers for corporate boardrooms, we elevate human perception of physical space.",
    ],
    keyCapabilities: [
      {
        title: "High-CRI Retail Display Lighting",
        desc: "CRI 95+ LED fixtures that render true fabric colors, diamonds, and gourmet food.",
      },
      {
        title: "Bespoke Chandeliers & Feature Lights",
        desc: "Handcrafted glass, brass, and acrylic decorative luminaires engineered in-house.",
      },
      {
        title: "Architectural Recessed & Linear Tracks",
        desc: "Frameless trimless channels, magnetic tracks, and cove accent lighting.",
      },
      {
        title: "Smart Lighting Control & DALI Systems",
        desc: "Automated scene dimming, daylight harvesting, and energy management.",
      },
    ],
    process: [
      {
        step: "01",
        title: "Dialux Lighting Simulation",
        desc: "Calculating lux levels, glare ratings (UGR), and uniform illumination maps.",
      },
      {
        step: "02",
        title: "Luminaire Specification",
        desc: "Selecting optics, color temperatures (2700K-5000K), and LED chip sets.",
      },
      {
        step: "03",
        title: "Custom Fabrication",
        desc: "Manufacturing custom housings, aluminum extrusions, and decorative frames.",
      },
      {
        step: "04",
        title: "Focusing & Programming",
        desc: "On-site beam alignment, angle tuning, and lighting scene programming.",
      },
    ],
    benefits: [
      {
        title: "In-House Luminaire Division",
        desc: "No long import lead times for specialty custom chandeliers and recessed profiles.",
      },
      {
        title: "Product Focus & Sales Boost",
        desc: "Optimized retail light spectrum proven to enhance customer engagement and sales.",
      },
      {
        title: "5-Year Commercial Warranty",
        desc: "Heavy-duty driver and LED chip selections designed for 14-hour daily mall operations.",
      },
    ],
    faqs: [
      {
        question: "Why is High-CRI important for retail boutiques?",
        answer:
          "High CRI (Color Rendering Index) ensures clothing and gold jewelry show their true, rich colors without yellowish or dull casts.",
      },
      {
        question: "Can you retrofit existing spaces with LED luminaires?",
        answer:
          "Yes, our Abacus Luminaire division performs seamless energy-saving LED upgrades for existing stores and offices.",
      },
    ],
    relatedProjects: [
      {
        title: "MaxMara Boutique Architectural Lighting",
        category: "Retail",
        location: "Marassi Galleria, Bahrain",
        image:
          "https://vibe.filesafe.space/1785525013889768799/attachments/b30bbccc-6cf2-4d9f-9df9-3bbb6a88d81f.jpg",
        slug: "maxmara-boutique-marassi-galleria",
      },
      {
        title: "Carter & White Showroom Lighting",
        category: "Luxury Retail",
        location: "Manama, Bahrain",
        image:
          "https://vibe.filesafe.space/1785525013889768799/attachments/1793aaf0-fc15-48ae-9594-477dbf1fddd6.jpg?v=2",
        slug: "carter-white-boutique-marassi-galleria",
      },
    ],
  },

  "digital-signage-display": {
    slug: "digital-signage-display",
    title: "Digital Solutions",
    seoTitle: "Digital Signage & Display in Bahrain",
    seoDescription:
      "Digital signage, video walls, interactive kiosks and Mirror TVs integrated into interior architecture in Bahrain. Cloud CMS and 24/7 commercial displays.",
    category: "Specialist Division",
    subtitle:
      "Digital kiosks, video walls, Mirror TVs, LED tickers, and dynamic digital brand experiences.",
    description:
      "Digital kiosks, video walls, Mirror TVs, LED tickers and dynamic brand experiences that fuse digital technology with interior architecture.",
    bullets: [
      "Fine-Pitch Video Walls",
      "Interactive Digital Kiosks",
      "Mirror TV Architectural Displays",
    ],
    imageAlt:
      "Digital signage and interactive kiosk display in mall - Abacus Bahrain",
    heroImage:
      "https://vibe.filesafe.space/1785525013889768799/attachments/99b433d0-d309-4ff0-ae7f-de73988c3ea9.jpg",
    gallery: [
      "https://vibe.filesafe.space/1785525013889768799/attachments/92ccc896-1131-4992-a12e-1c221ccbf983.png",
      "https://vibe.filesafe.space/1785525013889768799/attachments/5a04c92a-2a1e-4339-824d-39868cb2c6a1.png",
      "https://vibe.filesafe.space/1785525013889768799/attachments/f5e2ffd3-b072-411f-86d2-911da7611db0.jpg",
      "https://vibe.filesafe.space/1785525013889768799/attachments/dc77d658-df51-4cc6-8d4a-6826f9ef1706.jpg",
    ],
    iconName: "Monitor",
    overview: [
      "Modern physical spaces require dynamic digital engagement. Our Digital Solutions division integrates high-definition video walls, interactive touchscreen kiosks, seamless Mirror TVs, and curved LED banners into interior architecture.",
      "We ensure hardware enclosure, thermal ventilation, cable management, and content management software function harmoniously with the overall interior aesthetic.",
    ],
    keyCapabilities: [
      {
        title: "Seamless Fine-Pitch Video Walls",
        desc: "Ultra-narrow bezel and direct-view micro-LED displays for high-impact store storefronts.",
      },
      {
        title: "Interactive Digital Kiosks",
        desc: "Bespoke enclosure design incorporating touch screens, wayfinding, and receipt printers.",
      },
      {
        title: "Mirror TV & Architectural Displays",
        desc: "Vanishing television displays hidden behind mirrored glass for luxury hotel suites and VIP lounges.",
      },
      {
        title: "CMS & Content Distribution",
        desc: "Cloud-based remote content management allowing instant promotional updates across multiple branches.",
      },
    ],
    process: [
      {
        step: "01",
        title: "Spatial Tech Integration",
        desc: "Determining display resolution, viewing distance, and ambient light conditions.",
      },
      {
        step: "02",
        title: "Enclosure Engineering",
        desc: "Designing custom metal/wood framing with concealed service access hatches.",
      },
      {
        step: "03",
        title: "Hardware & Network Setup",
        desc: "Installing commercial-grade displays, media players, and data links.",
      },
      {
        step: "04",
        title: "Testing & CMS Onboarding",
        desc: "Calibrating display brightness, color uniformity, and training client teams on CMS software.",
      },
    ],
    benefits: [
      {
        title: "Clean Architectural Fit",
        desc: "No ugly hanging cables or exposed brackets-displays are flush-mounted into custom joinery.",
      },
      {
        title: "Commercial 24/7 Reliability",
        desc: "Industrial-grade panels with active cooling designed for continuous operation.",
      },
      {
        title: "One Point of Accountability",
        desc: "Both the digital display hardware and timber surround are supplied and backed by Abacus.",
      },
    ],
    faqs: [
      {
        question: "Can we control the displays remotely from another country?",
        answer:
          "Yes, our cloud CMS enables real-time content management from anywhere in the world.",
      },
      {
        question: "What is a Mirror TV?",
        answer:
          "It is an ultra-bright commercial display installed behind specialized optical mirror glass-when turned off, it appears as an elegant mirror.",
      },
    ],
    relatedProjects: [
      {
        title: "Hackett London Digital Retail Signage",
        category: "Aviation/Retail",
        location: "City Centre Bahrain",
        image:
          "https://vibe.filesafe.space/1785525013889768799/attachments/72126767-d769-4b71-8fbe-4d2425bc39fc.png",
        slug: "hackett-london-retail-outlet",
      },
      {
        title: "Al Salam Bank Interactive Kiosks",
        category: "Banking",
        location: "Manama, Bahrain",
        image:
          "https://vibe.filesafe.space/1785525013889768799/attachments/e49f393e-5323-4f8a-b548-d78b8aa7058e.png",
        slug: "al-salam-bank-al-raya-mall-branch",
      },
    ],
  },

  "signage-decorative-metalwork": {
    slug: "signage-decorative-metalwork",
    title: "Signage & Decorative Elements",
    seoTitle: "Architectural Signage & Metalwork in Bahrain",
    seoDescription:
      "Architectural signage, 3D illuminated lettering, PVD brass metalwork and CNC feature walls designed and installed in Bahrain for retail and corporate interiors.",
    category: "Specialist Division",
    subtitle:
      "Indoor and outdoor architectural signage, metal embellishments, CNC carving, and 3D feature walls.",
    description:
      "Indoor and outdoor signage, 3D backlit lettering, metal decorative work, CNC carving and architectural embellishments.",
    bullets: [
      "3D Illuminated Channel Letters",
      "PVD Brass & Titanium Metalwork",
      "CNC 3D Texture Panels",
    ],
    imageAlt: "Architectural signage and decorative metalwork Bahrain - Abacus",
    heroImage:
      "https://vibe.filesafe.space/1785525013889768799/attachments/1bed9bf6-a0bf-4c43-baf1-be04c024156c.jpg",
    gallery: [
      "https://assets.cdn.filesafe.space/TkZSxIjkCDLzrMPXgP9Q/media/6aa45fdecbbb25381190c8ee.jpg",
      "https://assets.cdn.filesafe.space/TkZSxIjkCDLzrMPXgP9Q/media/6aa45fdef4e2fd8aa0ff7e31.jpg",
      "https://assets.cdn.filesafe.space/TkZSxIjkCDLzrMPXgP9Q/media/6aa45fdea65c58fb9c471f02.jpg",
      "https://assets.cdn.filesafe.space/TkZSxIjkCDLzrMPXgP9Q/media/6aa45fde770fd0c165ac337b.jpg",
      "https://assets.cdn.filesafe.space/TkZSxIjkCDLzrMPXgP9Q/media/6aa45fdeac64f2f5e5c0e24f.jpg",
      "https://assets.cdn.filesafe.space/TkZSxIjkCDLzrMPXgP9Q/media/6aa45fdecbbb25381190c8ef.jpg",
      "https://vibe.filesafe.space/1785525013889768799/attachments/472c0074-e844-45c0-b62d-efd2b907b466.jpg",
    ],
    iconName: "Sparkles",
    overview: [
      "Your signage and decorative accents define your brand's physical handshake with customers. Abacus designs, fabricates, and installs premium indoor and outdoor signage, 3D backlit lettering, etched brass plaques, and ornate CNC wall panels.",
      "Utilizing laser cutting, waterjet technology, PVD metal coating, and expert hand assembly, we produce signage that passes stringent mall landlord design approvals and outdoor weather endurance tests.",
    ],
    keyCapabilities: [
      {
        title: "3D Illuminated Channel Letters",
        desc: "Halo-lit, face-lit, and edge-lit acrylic and stainless steel lettering with premium LEDs.",
      },
      {
        title: "PVD Brass & Titanium Metalwork",
        desc: "High-end brushed or polished brass, champagne gold, and rose gold architectural accents.",
      },
      {
        title: "CNC 3D Texture Panels",
        desc: "Intricately carved wood, MDF, GRC, and aluminum feature wall panels.",
      },
      {
        title: "Wayfinding & Environmental Graphics",
        desc: "Complete directional signage systems for shopping malls, corporate towers, and airports.",
      },
    ],
    process: [
      {
        step: "01",
        title: "Scale Mockups & Prototyping",
        desc: "Creating physical material samples and scale letter cutouts for landlord approval.",
      },
      {
        step: "02",
        title: "Laser & Waterjet Cutting",
        desc: "Cutting stainless steel, brass, acrylic, and hardwoods with sub-millimetre precision.",
      },
      {
        step: "03",
        title: "Assembly & Illumination Wiring",
        desc: "Integrating IP-rated LED modules, diffusers, and transformer power packs.",
      },
      {
        step: "04",
        title: "Precision Site Installation",
        desc: "Concealed stud mounting and alignment ensuring laser-straight positioning.",
      },
    ],
    benefits: [
      {
        title: "Mall Landlord Approval Mastery",
        desc: "We know exact material and lux level restrictions for every major mall in Bahrain.",
      },
      {
        title: "Weather Resistant Finishes",
        desc: "Outdoor signage built to withstand Gulf humidity, salt air, and intense UV exposure.",
      },
      {
        title: "Bespoke Metal & Wood Craft",
        desc: "Merging hand polishing craftsmanship with heavy industrial laser machinery.",
      },
    ],
    faqs: [
      {
        question: "What materials do you recommend for luxury store signage?",
        answer:
          "Brushed brass with warm white (3000K) halo backlighting creates an undeniable impression of luxury.",
      },
      {
        question: "Do you handle outdoor shop facade signs?",
        answer:
          "Yes, fully compliant with municipal regulations and structural wind load calculations.",
      },
    ],
    relatedProjects: [
      {
        title: "illy Storefront Signage",
        category: "Retail/F&B",
        location: "City Centre Bahrain",
        image:
          "https://vibe.filesafe.space/1785525013889768799/attachments/c9d4fce9-62f0-48c8-8dec-c769083f2da7.jpg?v=2",
        slug: "illy-cafe-city-centre",
      },
      {
        title: "MaxMara Marassi Galleria Facade",
        category: "Retail",
        location: "Marassi Galleria, Bahrain",
        image:
          "https://vibe.filesafe.space/1785525013889768799/attachments/b30bbccc-6cf2-4d9f-9df9-3bbb6a88d81f.jpg",
        slug: "maxmara-boutique-marassi-galleria",
      },
    ],
  },

  "commercial-renovation-refurbishment": {
    slug: "commercial-renovation-refurbishment",
    title: "Renovation & Refurbishment",
    seoTitle: "Renovation & Refurbishment in Bahrain",
    seoDescription:
      "Commercial renovation and refurbishment in Bahrain with minimal disruption. Overnight fast-track works for operating retail, offices and restaurants.",
    category: "Specialist Division",
    subtitle:
      "Modernising operating retail shops, offices, and restaurants with minimal operational disruption.",
    description:
      "Modernising existing operating shops, offices, and restaurants with minimal operational disruption and fast-track night shifts.",
    bullets: [
      "Overnight Fast-Track Execution",
      "Phased Live-Environment Works",
      "Minimal Revenue Interruption",
    ],
    imageAlt:
      "Luxury jewellery boutique interior refurbishment Bahrain - Abacus",
    heroImage:
      "https://vibe.filesafe.space/1785525013889768799/attachments/d277d40e-e41d-4b57-bade-9cd553926081.jpg",
    gallery: [
      "https://vibe.filesafe.space/1785525013889768799/attachments/5c18f02b-8354-437e-9abc-139bf08d1184.png",
      "https://vibe.filesafe.space/1785525013889768799/attachments/9614542b-3544-4b04-87f1-7a4999ed85d3.png",
      "https://vibe.filesafe.space/1785525013889768799/attachments/9bcb0191-9cb8-4afc-8882-7179793264e9.png",
    ],
    iconName: "RefreshCw",
    overview: [
      "In commercial real estate, downtime equals lost revenue. Our specialized Renovation & Refurbishment team transforms existing retail outlets, corporate offices, and hospitality venues quickly, cleanly, and safely.",
      "Whether conducting an overnight brand refresh or executing phased sectional renovations while your business remains open, we minimize downtime while maximizing impact.",
    ],
    keyCapabilities: [
      {
        title: "Overnight Fast-Track Refurbishments",
        desc: "Executing wall transformations, flooring replacements, and new display installation outside business hours.",
      },
      {
        title: "Phased Live-Environment Works",
        desc: "Sectional dust-proof hoarding allowing ongoing client operations in adjacent zones.",
      },
      {
        title: "Acoustic & Partition Reconfigurations",
        desc: "Adapting modern open-plan office layouts to create new private executive suites.",
      },
      {
        title: "MEP & Lighting Modernization",
        desc: "Replacing outdated halogen track lights with energy-efficient smart LED systems.",
      },
    ],
    process: [
      {
        step: "01",
        title: "Existing Site Condition Audit",
        desc: "Evaluating existing structural, MEP, and wall conditions to prevent unforeseen surprises.",
      },
      {
        step: "02",
        title: "Dust-Proof Hoarding Setup",
        desc: "Erecting clean, branded isolation barriers to protect ongoing staff and customer safety.",
      },
      {
        step: "03",
        title: "Off-Site Pre-Fabrication",
        desc: "Pre-building custom joinery elements in our Askar factory for 1-night site installation.",
      },
      {
        step: "04",
        title: "Rapid Execution & Handover",
        desc: "Continuous round-the-clock shift work ensuring doors open on schedule the next morning.",
      },
    ],
    benefits: [
      {
        title: "Minimal Revenue Interruption",
        desc: "Engineered schedules specifically built around your least busy hours or nights.",
      },
      {
        title: "Pre-Fabrication Advantage",
        desc: "80% of fabrication occurs in our factory, reducing site noise and dust.",
      },
      {
        title: "Cost-Effective Asset Refresh",
        desc: "Dramatically boost footfall and brand value without the cost of a full demolition.",
      },
    ],
    faqs: [
      {
        question:
          "Can you renovate our store overnight without closing during the day?",
        answer:
          "Yes! Many of our retail clients undergo overnight cosmetic upgrades and open for normal business at 10 AM.",
      },
      {
        question:
          "How do you manage dust and noise during office refurbishments?",
        answer:
          "We use sealed negative-pressure acoustic hoarding and heavy-duty HEPA air scrubbers.",
      },
    ],
    relatedProjects: [
      {
        title: "Executive Office Modernization",
        category: "Corporate",
        location: "Diplomatic Area, Bahrain",
        image:
          "https://vibe.filesafe.space/1785525013889768799/attachments/e49f393e-5323-4f8a-b548-d78b8aa7058e.png",
        slug: "al-salam-bank-al-raya-mall-branch",
      },
      {
        title: "Puma Retail Store Face-Lift",
        category: "Luxury Retail",
        location: "City Centre Bahrain",
        image:
          "https://vibe.filesafe.space/1785525013889768799/attachments/66ed7074-04bb-4724-8c55-02f874d8d0ab.png",
        slug: "puma-concept-store-city-centre",
      },
    ],
  },
};

// Map short legacy slugs to long slugs for backwards compatibility
export const SLUG_ALIASES: Record<string, string> = {
  "interior-design-bahrain": "interior-design",
  "fit-out-contracting-bahrain": "fit-out-contracting",
  "custom-joinery-bahrain": "custom-joinery",
  "mep-contracting-bahrain": "mep-contracting",
  "architectural-lighting-bahrain": "architectural-lighting",
  "digital-signage-display-bahrain": "digital-signage-display",
  "signage-decorative-metalwork-bahrain": "signage-decorative-metalwork",
  "commercial-renovation-refurbishment-bahrain":
    "commercial-renovation-refurbishment",
  "interior-design": "interior-design",
  "fit-out-turnkey": "fit-out-contracting",
  "joinery-custom-furniture": "custom-joinery",
  "mep-solutions": "mep-contracting",
  "lighting-luminaire": "architectural-lighting",
  "digital-solutions": "digital-signage-display",
  "signage-decorative": "signage-decorative-metalwork",
  "renovation-refurbishment": "commercial-renovation-refurbishment",
};
